import type React from 'react';
import { memo, useEffect } from 'react';
import { useLayering } from '@atlaskit/layering';
import { useActiveCell, useFocusVisible, useGridRef } from '../../controllers/grid/index.tsx';
import { useContainerRef, useViewport } from '../../controllers/container/index.tsx';
import { useScrollIntoView } from './utils.tsx';

interface Props {
	paddingTop?: number;
	paddingRight?: number;
	paddingBottom?: number;
	paddingLeft?: number;
}

export const ScrollIntoView: React.FC<Props> = memo(
	({ paddingTop, paddingRight, paddingBottom, paddingLeft }) => {
		const [gridRef, { withState }] = useGridRef();

		const [focusVisible] = useFocusVisible();
		const [{ row: activeRow, column: activeColumn }] = useActiveCell();

		const { isLayerDisabled } = useLayering();

		// Grab necessary state and actions from the container
		const [viewport, { scrollTo }] = useViewport();
		const [containerRef] = useContainerRef();

		const scrollIntoView = useScrollIntoView({
			containerRef,
			viewport,
			scrollTo,
			scrollPadding: {
				top: paddingTop,
				right: paddingRight,
				bottom: paddingBottom,
				left: paddingLeft,
			},
		});

		useEffect(() => {
			// Scroll focused element into view when it's a row or a cell
			// handling both non-virtualized and virtualized elements
			if (focusVisible) {
				withState(scrollIntoView(activeRow, activeColumn));
			}
		}, [activeColumn, activeRow, focusVisible, scrollIntoView, withState]);

		useEffect(() => {
			const gridEl = gridRef.current;

			if (!gridEl) {
				return;
			}

			const handleFocus = (e: FocusEvent) => {
				if (!(e.target instanceof HTMLElement) || isLayerDisabled()) {
					return;
				}

				try {
					if (!e.target.matches(':focus-visible')) {
						return;
					}
				} catch (_) {
					// Ignore errors thrown from testing environments
				}

				const stripNaN = (number: number) => (Number.isNaN(number) ? undefined : number);
				const getIndex = (ariaValue: string) => stripNaN(parseInt(ariaValue ?? '', 10) - 1);

				if (e.target === e.target.closest('td, th, tr')) {
					// Should be handle on the effect above
					return;
				}

				const column = getIndex(e.target.closest('td, th')?.getAttribute('data-colindex') ?? '');
				const row = getIndex(e.target.closest('tr')?.getAttribute('aria-rowindex') ?? '');

				if (row !== undefined) {
					withState(scrollIntoView(row, column ?? -1, e.target));
				}
			};

			gridEl.addEventListener('focusin', handleFocus);
			return () => gridEl.removeEventListener('focusin', handleFocus);
		}, [gridRef, isLayerDisabled, scrollIntoView, withState]);

		return null;
	},
);
